import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * Renders documents (PDF, DOCX, text/markdown) with fullscreen support
 * and blob URL lifecycle management.
 */
@Component({
	selector: 'app-document-viewer',
	templateUrl: './document-viewer.component.html',
	styleUrls: ['./document-viewer.component.scss'],
})
export class DocumentViewerComponent implements OnInit, OnChanges, OnDestroy {
	// ─── Inputs ────────────────────────────────────────────────────────────────
	@Input() blob: Blob | null = null;
	@Input() contentType: string | null = null;
	@Input() title: string | null = null;

	@Output() download = new EventEmitter<void>();

	// ─── State ─────────────────────────────────────────────────────────────────
	docxFullscreen = false;
	pdfFullscreen = false;
	blobUrl: SafeResourceUrl | null = null;
	documentText: string | null = null;

	private rawBlobUrl: string | null = null;

	@ViewChild('docxContainer') docxContainer!: ElementRef;

	// ─── Computed getters ──────────────────────────────────────────────────────

	/** Whether the document is a PDF */
	get isPdf(): boolean {
		return !!this.contentType && this.contentType.includes('pdf');
	}

	/** Whether the document is a DOCX */
	get isDocx(): boolean {
		return !!this.contentType && (this.contentType.includes('officedocument') || this.contentType.includes('msword'));
	}

	/** Whether the document is text/markdown */
	get isText(): boolean {
		return !!this.contentType && (this.contentType.includes('text') || this.contentType.includes('markdown'));
	}

	constructor(private sanitizer: DomSanitizer) {}

	// ─── Lifecycle ─────────────────────────────────────────────────────────────

	ngOnInit(): void {
		this.setupBlob();
	}

	ngOnChanges(changes: SimpleChanges): void {
		const blobChanged = changes['blob'] && !changes['blob'].isFirstChange();
		const contentTypeChanged = changes['contentType'] && !changes['contentType'].isFirstChange();
		if (blobChanged || contentTypeChanged) {
			this.clearDocxContainer();
			this.setupBlob();
		}
	}

	ngOnDestroy(): void {
		this.revokeBlobUrl();
	}

	// ─── Public actions ────────────────────────────────────────────────────────

	/** Triggers a download of the raw blob file */
	onDownload(): void {
		this.download.emit();
	}

	// ─── Private helpers ───────────────────────────────────────────────────────

	/** Creates a blob URL and initializes content based on document type */
	private setupBlob(): void {
		if (!this.blob) return;

		this.revokeBlobUrl();
		const url = URL.createObjectURL(this.blob);
		this.rawBlobUrl = url;
		this.blobUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);

		if (this.isDocx) {
			this.renderDocx(this.blob);
		} else if (this.isText) {
			this.blob.text().then((text) => {
				this.documentText = text;
			});
		}
	}

	/** Renders a DOCX blob using the docx-preview library */
	private async renderDocx(blob: Blob): Promise<void> {
		const { renderAsync } = await import('docx-preview');
		setTimeout(() => {
			const container = this.docxContainer?.nativeElement;
			if (container) {
				renderAsync(blob, container, undefined, {
					className: 'docx-preview-content',
					inWrapper: true,
					ignoreWidth: true,
					ignoreHeight: true,
				});
			}
		}, 100);
	}

	/** Revokes the current blob URL to prevent memory leaks */
	private revokeBlobUrl(): void {
		if (this.rawBlobUrl) {
			URL.revokeObjectURL(this.rawBlobUrl);
			this.rawBlobUrl = null;
			this.blobUrl = null;
		}
	}

	/** Clears the DOCX container DOM content before rendering a new document */
	private clearDocxContainer(): void {
		const container = this.docxContainer?.nativeElement;
		if (container) {
			container.innerHTML = '';
		}
	}
}
